import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  RowSelectionState,
  ColumnFiltersState,
  OnChangeFn,
} from "@tanstack/react-table";
import { PulseLoader } from "react-spinners";

interface TablesV2Props<TData extends object> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  isLoading?: boolean;
  searchQuery?: string;
  enableColumnFilters?: boolean;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  canSelectRow?: (row: TData) => boolean;
  getRowId?: (row: TData, index: number) => string;
  emptyMessage?: string;
  loadingMessage?: string;
  pageSize?: number;
  columnFilterPlaceholder?: string;
}

export function TablesV2<TData extends object>({
  data,
  columns,
  isLoading = false,
  searchQuery = "",
  enableColumnFilters = false,
  columnFilters: externalColumnFilters,
  onColumnFiltersChange: externalOnColumnFiltersChange,
  rowSelection,
  onRowSelectionChange,
  canSelectRow,
  getRowId,
  emptyMessage = "No records found matching your current search criteria.",
  loadingMessage = "Loading records...",
  pageSize = 15,
  columnFilterPlaceholder = "Filter...",
}: TablesV2Props<TData>) {
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFiltersState>([]);

  const activeColumnFilters = externalColumnFilters ?? internalColumnFilters;
  const activeOnColumnFiltersChange = externalOnColumnFiltersChange ?? setInternalColumnFilters;

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter: searchQuery,
      columnFilters: activeColumnFilters,
      ...(rowSelection ? { rowSelection } : {}),
    },
    enableColumnFilters,
    onColumnFiltersChange: activeOnColumnFiltersChange,
    enableRowSelection: canSelectRow ? (row) => canSelectRow(row.original) : Boolean(rowSelection),
    ...(onRowSelectionChange ? { onRowSelectionChange } : {}),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...(getRowId ? { getRowId } : {}),
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  return (
    <div className="contact-table-card">
      {isLoading ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
          <PulseLoader color="#EE3F7C" size={14} />
          <span className="fs-12px font-mono contact-sub-text">{loadingMessage}</span>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="contact-table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <React.Fragment key={headerGroup.id}>
                    <tr>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                          style={{ cursor: header.column.getCanSort() ? "pointer" : "default", userSelect: "none" }}
                          title={header.column.getCanSort() ? "Sort column" : undefined}
                        >
                          {header.isPlaceholder ? null : (
                            <span className="d-inline-flex align-items-center gap-1">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {{
                                asc: " ↑",
                                desc: " ↓",
                              }[header.column.getIsSorted() as string] ?? (
                                header.column.getCanSort() ? (
                                  <span style={{ opacity: 0.35, fontSize: 10 }} aria-hidden>
                                    ↕
                                  </span>
                                ) : null
                              )}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                    {enableColumnFilters && (
                      <tr className="v2-filter-row">
                        {headerGroup.headers.map((header) => (
                          <th key={`filter-${header.id}`} style={{ padding: "6px 12px 10px 12px" }}>
                            {header.column.getCanFilter() ? (
                              <input
                                type="text"
                                placeholder={columnFilterPlaceholder}
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                style={{
                                  width: "100%",
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                  borderRadius: "6px",
                                  background: "rgba(255, 255, 255, 0.06)",
                                  border: "1px solid rgba(255, 255, 255, 0.12)",
                                  color: "#ffffff",
                                  outline: "none",
                                  transition: "border 0.2s, background 0.2s",
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = "#EE3F7C";
                                  e.target.style.background = "rgba(255, 255, 255, 0.1)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = "rgba(255, 255, 255, 0.12)";
                                  e.target.style.background = "rgba(255, 255, 255, 0.06)";
                                }}
                              />
                            ) : null}
                          </th>
                        ))}
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-5 contact-sub-text fs-13px">
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className={row.getIsSelected() ? "selected" : ""}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {table.getPageCount() > 1 && (
            <div className="d-flex align-items-center justify-content-between p-3 border-top border-white-5 fs-12px">
              <div className="contact-sub-text">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="contact-action-btn py-1 px-3 fs-11px"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="contact-action-btn py-1 px-3 fs-11px"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TablesV2;
